<script setup lang="ts">
/**
 * The frames nobody rewrote, drawn word for word.
 *
 * SVG text, because SVG places text by its baseline, which is what a PDF
 * records: no font's ascent or line box gets between the number and the ink.
 *
 * In the document's own font, every glyph lands where it was printed: the
 * designer's tracking becomes letter spacing, and a kerned letter is placed
 * explicitly (see `segmentsOf`). In a stand-in, whose glyphs are not the same
 * width, each word is fitted back into the width it took instead, so a line
 * still ends where it did.
 *
 * Still text: selectable, searchable, and exported as text.
 */
import { fontStack, fontVariant, isExact, type ImportedFont } from '@/domain/facsimile/fonts'
import { frameTurn, segmentsOf, shownText } from '@/domain/facsimile/text'
import type { FacsimileFrame, FacsimileRun } from '@/domain/facsimile/types'

const props = defineProps<{
  frames: readonly FacsimileFrame[]
  width: number
  height: number
  fonts: ReadonlyMap<string, ImportedFont>
}>()

function exact(run: FacsimileRun): boolean {
  return isExact(props.fonts.get(run.font))
}

function runAttributes(run: FacsimileRun): Record<string, string | number> {
  const font = props.fonts.get(run.font)
  const variant = fontVariant(font)
  const placed = exact(run)
    ? { 'letter-spacing': run.tracking }
    : { textLength: run.width, lengthAdjust: 'spacingAndGlyphs' }
  return {
    'font-family': fontStack(font),
    'font-weight': variant.weight,
    'font-style': variant.style,
    'font-size': run.size,
    fill: run.colour,
    ...placed,
  }
}
</script>

<template>
  <svg
    class="facsimile-text"
    :viewBox="`0 0 ${width} ${height}`"
    :width="`${width}pt`"
    :height="`${height}pt`"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g v-for="frame in frames" :key="frame.id" :data-frame="frame.id" :transform="frameTurn(frame)">
      <template v-for="(line, index) in frame.lines" :key="index">
        <!-- Text through v-text only: these elements keep white space as written,
             so no template indentation may ever reach them. -->
        <template v-for="(run, position) in line.runs" :key="position">
          <text
            v-if="exact(run) && (run.anchors.length > 0 || run.ligatures.length > 0)"
            :x="run.x"
            :y="run.baseline"
            v-bind="runAttributes(run)"
          >
            <tspan
              v-for="(segment, at) in segmentsOf(run)"
              :key="at"
              :x="segment.x"
              :letter-spacing="segment.ligature ? 0 : undefined"
              v-text="segment.text"
            />
          </text>
          <text
            v-else
            :x="run.x"
            :y="run.baseline"
            v-bind="runAttributes(run)"
            v-text="shownText(run.text)"
          />
        </template>
      </template>
    </g>
  </svg>
</template>

<style scoped>
.facsimile-text {
  position: absolute;
  inset: 0;
  overflow: visible;
  pointer-events: none;
  /* Ligatures as the PDF set them; no kerning of the browser's own, since the
     positions already carry the PDF's. */
  font-kerning: none;
  font-variant-ligatures: common-ligatures;
  text-rendering: geometricPrecision;
}

.facsimile-text text {
  white-space: pre;
}
</style>
